"use client";

import StyledIcon from "@litonarefin/components/StyledIcon";
import { cn } from "@litonarefin/utils";
import { getSourceSite } from "@litonarefin/utils/getSourceSite";
import { getIcon } from "@litonarefin/utils/icons";
import { usePathname } from "next/navigation";
import React, { useState, useEffect, useRef, useCallback } from "react";

const siteSource = getSourceSite();

const SideTableOfContent = () => {
  const [activeContent, setActiveContent] = useState(true);
  const [headings, setHeadings] = useState([]);
  const [show, setShow] = useState(false);
  const [activeId, setActiveId] = useState(""); // Track active heading ID

  const pathname = usePathname();
  const observerRef = useRef(null);

  // Scroll Percentage
  const scrollPercentage = () => {
    const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
    const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = (winScroll / height) * 150;

    if (scrolled && scrolled > 10) {
      setShow(true);
    } else {
      setShow(false);
    }
  };

  // Method 1: Using Intersection Observer (Recommended)
  const setupIntersectionObserver = useCallback(() => {
    if (!headings.length) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      rootMargin: "-100px 0px -66% 0px", // Adjust these values based on your header height
      threshold: 0, // Trigger as soon as any part of the heading becomes visible
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, options);

    // Observe each heading
    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observerRef.current.observe(element);
      }
    });
  }, [headings]);

  // Method 2: Using scroll event (Alternative)
  const setupScrollListener = useCallback(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for header height
      
      // Find the current active heading
      let currentActiveId = "";
      for (let i = 0; i < headings.length; i++) {
        const element = document.getElementById(headings[i].id);
        if (element && element.offsetTop <= scrollPosition) {
          currentActiveId = headings[i].id;
        } else {
          break;
        }
      }
      
      if (currentActiveId && currentActiveId !== activeId) {
        setActiveId(currentActiveId);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [headings, activeId]);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll(".jlt-blog-content h2")).map(
      (elem, i) => {
        if (!elem.getAttribute("id")) {
          elem.setAttribute("id", `heading_${++i}`);
        }
        return {
          id: elem.id,
          text: elem.innerText,
        };
      }
    );
    setHeadings(elements);

    // Show Hide Table of Content
    const isHiddenData = localStorage.getItem(`${siteSource}_blog_t_content`);
    if (isHiddenData) {
      const isHidden = JSON.parse(isHiddenData);
      if (isHidden && activeContent) {
        setActiveContent(false);
      }
    }

    window.addEventListener("scroll", scrollPercentage);

    return () => {
      window.removeEventListener("scroll", scrollPercentage);
    };
  }, []);

  // Setup active heading tracking after headings are loaded
  useEffect(() => {
    if (headings.length) {
      // Choose one method (Intersection Observer is recommended)
      setupIntersectionObserver();
      // setupScrollListener(); // Alternative method
      
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }
  }, [headings, setupIntersectionObserver]);

  if (!headings?.length) return;

  // Render function for the list items to avoid duplication
  const renderTableOfContentItems = () => (
    <ul className="jlt-pr-3 jlt-overflow-x-hidden jlt-overflow-y-auto [&::-webkit-scrollbar]:jlt-w-1 [&::-webkit-scrollbar]:jlt-bg-gray-100 [&::-webkit-scrollbar-thumb]:jlt-bg-gray-300 jlt-flex jlt-flex-col jlt-gap-2">
      {headings.map((heading) => (
        <li
          key={heading.id}
          className="jlt-pl-3 jlt-pr-2 jlt-text-[12px] jlt-font-medium jlt-leading-[16px] jlt-text-foreground before:jlt-content-['-_']"
        >
          <a
            href={`#${heading.id}`}
            className={cn(
              "jlt-select-none jlt-transition-all jlt-duration-150",
              activeId === heading.id 
                ? "jlt-text-[var(--primary)] jlt-font-bold jlt-underline" 
                : "hover:jlt-text-[var(--primary)] hover:jlt-underline"
            )}
            onClick={(e) => {
              e.preventDefault();
              const element = document.querySelector(`#${heading.id}`);
              if (element) {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
                setActiveId(heading.id); // Immediately set active on click
              }
            }}
          >
            {heading.text}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="mb-3">
      <h4 className="jlt-text-[18px] jlt-font-bold jlt-text-foreground jlt-leading-5 jlt-mb-4">
        Table of Contents
      </h4>

      {renderTableOfContentItems()}

      {headings ? (
        <script
          type="application/ld+json"
          id="blog_table_of_content"
          dangerouslySetInnerHTML={{
            __html: `{"@context": "https://schema.org","@type": "ItemList","name": "Table of Contents","itemListElement": ${getTableOfContentItems(
              headings,
              pathname
            )}
            }`,
          }}
        />
      ) : null}
    </div>
  );
};

function getTableOfContentItems(headings, pathname) {
  const items = headings.map((heading, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: heading.text,
    url: `${process.env.NEXT_PUBLIC_NEXTJS_SITE_URL}${pathname}#${heading.id}`,
  }));

  return JSON.stringify(items);
}

export default SideTableOfContent;